import React, { useCallback } from 'react';
import NavLink from './NavLink';
import Logo from './Logo';
import { Menu, MenuItem, MenuLink, MenuSeparator, View, Text } from './ui';
import Media from 'react-media';
import Headroom from 'react-headroom';
import useDropdown from './hooks/useDropdown';
import useLogout from './hooks/useLogout';
import { hasFeature, hasUserRight, useCurrentUser } from '../state/zustand';
import { useCloseToasts } from './notifications/ToastNotifications';
import NotificationCenter from './notifications/NotificationCenter';

const NavBar = ({ children }: { children: React.ReactNode }) => {
  return (
    <View
      flexDirection="row"
      width={1}
      height={48}
      justifyContent="space-between"
      alignItems="center"
      bg="navbar.background"
      borderBottomColor="navbar.borderBottom"
      borderBottomWidth={1}
      paddingLeft={10}
      paddingRight={10}
    >
      {children}
    </View>
  );
};

const Navigation = () => {
  const currentUser = useCurrentUser();
  const isCharterer = currentUser?.organization?.charterer ?? false;
  const isRootOrgUser =
    typeof currentUser?.organization?.parentOrganizationId !== 'number';
  const isPlanner = currentUser?.userRights.nodes.some(
    (node) => node?.createAndEditVoyages,
  );
  const showAdmin = currentUser?.isAdmin || (isRootOrgUser && isPlanner);
  const emailOrName = currentUser?.myMail || currentUser?.name;

  const hasFinancialRights =
    currentUser &&
    hasUserRight(currentUser.organizationId, 'accessFinancialData');

  const {
    setMenuRef,
    setTargetRef,
    styles: menuStyles,
    isVisible: isMenuVisible,
    setVisibility: setMenuVisibility,
  } = useDropdown({ floatProps: { placement: 'bottom-end' } });

  const logOut = useLogout();

  const hasSapTools = hasFeature('sap_tools');

  const hasIntegration =
    hasFeature('q88_tools') ||
    hasFeature('veson_tools') ||
    hasFeature('shipnet_tools') ||
    hasFeature('shipnet_tools_oneway') ||
    hasFeature('preem_tools');

  const primaryNavLinks: { label: string; href: string }[] = [
    ...(hasSapTools
      ? [
          { label: 'SAP', href: '/sap' },
          { label: 'Incoming', href: '/incoming' },
        ]
      : []),
    ...((isCharterer && isPlanner) || (isRootOrgUser && isPlanner)
      ? [{ label: 'Planning', href: '/planning' }]
      : []),
    { label: 'Operations', href: '/operations' },
    { label: 'Orders', href: '/orders' },
    ...(hasIntegration ? [{ label: 'Logs', href: '/integration-logs' }] : []),
  ];

  const closeToasts = useCloseToasts();
  const clickHandler = useCallback(() => {
    closeToasts();
    setMenuVisibility(!isMenuVisible);
  }, [closeToasts, isMenuVisible, setMenuVisibility]);

  return currentUser ? (
    <Headroom style={{ zIndex: 200 }}>
      <NavBar>
        <Logo />
        <View style={{ flexDirection: 'row', flexShrink: 1 }}>
          <Media query="(min-width: 980px)">
            <>
              {primaryNavLinks.map((navLink) => (
                <NavLink
                  key={navLink.href}
                  href={navLink.href}
                  label={navLink.label}
                >
                  {navLink.label}
                </NavLink>
              ))}
              <NotificationCenter />
            </>
          </Media>
          <div ref={setTargetRef}>
            <View
              data-testid="mmm hamburger"
              flexDirection="row"
              height="47px"
              alignItems="center"
              justifyContent="center"
              ml={3}
              pl={3}
              pr={2}
              bg="navbar.background"
              onClick={clickHandler}
            >
              {emailOrName && (
                <Text color="neutral.white_50" mr={2} isUnselectable>
                  {emailOrName}
                </Text>
              )}
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 22"
                style={{ pointerEvents: 'none' }}
              >
                <path
                  fill="#fff"
                  d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"
                />
              </svg>
            </View>
          </div>
          <div ref={setMenuRef} style={{ ...menuStyles }}>
            {isMenuVisible && (
              <Menu hasShadow>
                <Media query="(max-width: 979px)">
                  <>
                    {primaryNavLinks.map((navLink) => (
                      <MenuLink
                        key={navLink.href}
                        label={navLink.label}
                        href={navLink.href}
                      />
                    ))}
                    <MenuSeparator />
                  </>
                </Media>
                {(isCharterer || isRootOrgUser) && (
                  <MenuLink label="Contracts" href="/contracts" />
                )}
                {hasFinancialRights && (
                  <MenuLink label="Archived" href="/archived" />
                )}
                <MenuLink label="Trash bin" href="/archive" />
                {showAdmin && <MenuLink label="Admin" href="/admin" />}
                <MenuSeparator />
                <MenuLink label="Manage account" href="/account" />
                <MenuItem label="Log out" onPress={logOut} />
                <MenuSeparator />
                <View p={3}>
                  <Text fontSize={1} fontWeight="bold" color="neutral.gray_340">
                    Need help?
                    <br />
                    Contact{' '}
                    <a href="mailto:support@seaber.io">support@seaber.io</a>
                  </Text>
                </View>
              </Menu>
            )}
          </div>
        </View>
      </NavBar>
    </Headroom>
  ) : null;
};

Navigation.displayName = 'Navigation';

export default Navigation;
